import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/services/api";

export type WorkflowRole = "writer" | "senior_reviewer";

export const WORKFLOW_ROLES: WorkflowRole[] = ["writer", "senior_reviewer"];

export const WORKFLOW_ROLE_LABELS: Record<WorkflowRole, string> = {
  writer: "Writer",
  senior_reviewer: "Senior Reviewer",
};

/** The project properties that hold each role in the project service; managed in the Team panel only. */
export const ROLE_PROPERTY_KEYS = ["writers", "senior_reviewers"];

export interface TeamMember {
  user_id: string;
  /** Project-service membership role. */
  role: "viewer" | "editor" | "owner";
  email: string;
  first_name: string;
  last_name: string;
  /** Workflow roles; a member may hold both. */
  workflow_roles: WorkflowRole[];
}

export interface Team {
  can_manage: boolean;
  members: TeamMember[];
}

/** `current` with `role` switched on or off, in the canonical order. */
export function toggleRole(current: WorkflowRole[], role: WorkflowRole, on: boolean): WorkflowRole[] {
  return WORKFLOW_ROLES.filter((r) => (r === role ? on : current.includes(r)));
}

const teamKey = (projectId: string) => ["pubcreator", "team", projectId] as const;

export function useTeam(projectId: string) {
  return useQuery({
    queryKey: teamKey(projectId),
    queryFn: () => api.get<Team>(`/api/projects/${projectId}/team`),
    enabled: !!projectId,
  });
}

export function useSetWorkflowRoles(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roles }: { userId: string; roles: WorkflowRole[] }) =>
      api.put<Team>(`/api/projects/${projectId}/team/${userId}`, { roles }),
    onSuccess: (team) => queryClient.setQueryData(teamKey(projectId), team),
    // The roles live on the project (as properties), so its cached copy is stale now.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["projects"] }),
  });
}
